import React from 'react';
import { useLocation } from 'react-router-dom';

import {
	BOOKING_APPOINTMENT,
	BOOKING_ARRANGEMENTS,
	BOOKING_CONFIRMATION,
	BOOKING_FAMILY,
	BOOKING_PAYMENT,
	BOOKING_PET,
	BOOKING_PHONE_VERIFICATION,
	BOOKING_SCHEDULE,
	LS_ONLINE_BOOKING_TRACKING,
} from '@app/utils/booking';
import { BookingContext } from '@app/components/Pages/Booking/Booking';
import { pushEvent } from '@app/components/TagManager';
import { AppointmentParentType, OnlineBookingLastStep } from '@app/objects/OnlineBooking';

enum Tracking {
	PhoneVerify = 0,
	FamilyInfo = 1,
	PetSelect = 2,
	PetInfo = 3,
	AppointmentType = 4,
	ArrangementsType = 5,
	ScheduleAppointment = 6,
	ScheduleArrivalInfo = 7,
	Payment = 8,
	Confirmation = 9,
}

interface OnlineBookingTracking {
	[Tracking.PhoneVerify]?: boolean;
	[Tracking.FamilyInfo]?: boolean;
	[Tracking.PetSelect]?: boolean;
	[Tracking.PetInfo]?: boolean;
	[Tracking.AppointmentType]?: boolean;
	[Tracking.ArrangementsType]?: boolean;
	[Tracking.ScheduleAppointment]?: boolean;
	[Tracking.ScheduleArrivalInfo]?: boolean;
	[Tracking.Payment]?: boolean;
	[Tracking.Confirmation]?: boolean;
}

const useOnlineBookingTracking = () => {
	const { pathname } = useLocation();
	const context = React.useContext(BookingContext);

	if (!context?.state) throw new Error('Error context');

	const {
		state: { item },
	} = context;

	if (!item) return;

	React.useEffect(() => {
		const localTracking: OnlineBookingTracking = JSON.parse(localStorage.getItem(LS_ONLINE_BOOKING_TRACKING) || '{}');

		const trackOnce = (key: Tracking, eventScreen: string) => {
			const stringKey = String(key);

			if (!localTracking?.[stringKey]) {
				localTracking[stringKey] = true;
				localStorage.setItem(LS_ONLINE_BOOKING_TRACKING, JSON.stringify(localTracking));
				pushEvent('onlineBookingScreen', { screen: eventScreen });

				if (item.selectedAppointmentType === AppointmentParentType.Teleadvice || !item.selectedDate) {
					pushEvent('onlineBookingErrorScreen', { screen: 'OLB_Telehospice' });
				}
			}
		};

		switch (pathname) {
			case BOOKING_PHONE_VERIFICATION:
				trackOnce(Tracking.PhoneVerify, 'phoneVerify');
				break;
			case BOOKING_FAMILY:
				trackOnce(Tracking.FamilyInfo, 'familyInfo');
				break;
			case BOOKING_PET:
				if (item.lastStep === OnlineBookingLastStep.Pet && !item?.petName?.length) {
					trackOnce(Tracking.PetSelect, 'petSelect');
				}
				if (item.lastStep === OnlineBookingLastStep.Pet && item?.petName?.length) {
					trackOnce(Tracking.PetInfo, 'petInfo');
				}
				break;
			case BOOKING_APPOINTMENT:
				trackOnce(Tracking.AppointmentType, 'appointmentType');
				break;
			case BOOKING_ARRANGEMENTS:
				trackOnce(Tracking.ArrangementsType, 'arrangementsType');
				break;
			case BOOKING_SCHEDULE:
				if (item.lastStep === OnlineBookingLastStep.Schedule) {
					trackOnce(Tracking.ScheduleAppointment, 'scheduleAppointment');
				}
				if (item.lastStep === OnlineBookingLastStep.Payment) {
					trackOnce(Tracking.ScheduleArrivalInfo, 'scheduleArrivalInfo');
				}
				break;
			case BOOKING_PAYMENT:
				trackOnce(Tracking.Payment, 'payment');
				break;
			case BOOKING_CONFIRMATION:
				trackOnce(Tracking.Confirmation, 'confirmation');
				break;
			default:
				break;
		}
	}, [location.pathname, item]);
};

export default useOnlineBookingTracking;
